/**
 * Interactive tutorials — static registry.
 *
 * Data, not components: a future tutorial is one more entry in `TOURS`, so the
 * Learn group in the profile slide-over and the overlay engine both render from
 * here rather than gaining a component each
 * (docs/INTERACTIVE_TUTORIALS_PLAN.md §2).
 *
 * `app/models/tutorials.rb` mirrors the display fields (id/title/subtitle/
 * minutes/steps) so the drawer can render without booting this bundle. The
 * checkpoint script below is NOT mirrored there — it exists once, here.
 *
 * ORDER FOLLOWS THE REAL APP, NOT THE HANDOFF README. The mock-up in
 * design_handoff_interactive_tutorial/ is a React replica whose forms differ
 * from BLUME's. Two sequences are deliberately different from the README's:
 *   · the new-event form renders client → name → date
 *     (app/views/events/_form_fields.html.erb), not name → client → date;
 *   · PricingFields.vue renders markup → buffer → labour, not markup → labour
 *     → buffer.
 * The copy per checkpoint is the README's; only the sequence changed, so the
 * spotlight always walks down a form and never back up. The app is not
 * reordered to match the mock-up.
 */

/**
 * Advance condition for a checkpoint.
 *
 * `look` spotlights a target the florist only needs to read — a cost panel —
 * and advances on the card's own Next, which is lit from the start.
 */
export type TourMode = 'click' | 'input' | 'intro' | 'look'

export interface TourCheckpoint {
  /** Zero-based index into the parent tour's `steps`. */
  step: number
  /** `data-tour` id of the element to spotlight. Absent on `intro`. */
  target?: string
  /** Secondary target that completes the same checkpoint (e.g. "Not yet"). */
  alt?: string
  mode: TourMode
  /**
   * Advance on the click itself instead of the usual short delay.
   *
   * A `click` checkpoint normally waits CLICK_ADVANCE_DELAY_MS so the app's own
   * handler runs first — the modal the click opens has to exist before the tour
   * points at a field inside it. That delay is a bet that this document is still
   * here 60ms later, and for a click that SUBMITS it is a bet that is lost
   * whenever the server is quick: the advanced index is never written, the next
   * page resumes on the checkpoint just finished, and the florist gets a docked
   * card pointing at a button that is on the previous screen.
   *
   * Declared per checkpoint rather than inferred from the target's markup
   * (`type="submit"`, or "is inside a <form>"). The engine would then be reading
   * intent out of a view it does not own: `send-confirm` is a plain button that
   * posts and stays, and a view that switched `add-arrangement` to a submit
   * would silently lose the grace period this delay exists for. What is actually
   * true — "this click ends the page" — belongs beside the copy, in the script.
   */
  advanceImmediately?: boolean
  /**
   * Label for an `intro` card's advance button. Defaults to "Show me", which
   * reads right when the card introduces a screen; an intro that instead waits
   * for the florist to finish something ("pick a flower, then…") wants "Next".
   */
  cta?: string
  /**
   * `data-tour` id of an element to click when this intro's button advances
   * the tour — the flower picker's Done, so Next on "Pick a flower" closes a
   * picker the florist left open rather than pointing at a screen behind it.
   * A no-op when the element is not on the page.
   */
  dismiss?: string
  /**
   * `data-tour` id of an element to click ONCE when this checkpoint's target is
   * not in the DOM but that element is — the newest arrangement card's toggle,
   * whose fields and cost panel only render while the card is open. A reload
   * mid-run (the editor reopens with every card folded) or a florist who
   * folded the card by hand is then shown the card opening rather than a tour
   * pointing at nothing. Clicked through the app's own handler, never by
   * reaching into the editor's state. Hannah, 2026-09-08.
   */
  reveal?: string
  /**
   * Which tab of the event editor the target lives on. When the target is not
   * in the DOM but this is set, the overlay spotlights that tab's `qstep-*`
   * button and says so, instead of docking and then skipping — a florist who
   * reloads mid-run (the editor reopens on Details) or wanders to another tab
   * is pointed back rather than silently moved on. Only for targets INSIDE a
   * tab's panel; the `qstep-*` buttons themselves are on every tab.
   */
  screen?: 'details' | 'arrangements' | 'review'
  title: string
  /**
   * Card body. Plain text with `**…**` for emphasis, rendered by an
   * inline-markdown-lite pass rather than v-html — this copy is expected to
   * become editable, and raw HTML here would be an injection surface.
   */
  body: string
}

export interface Tour {
  id: string
  title: string
  subtitle: string
  minutes: number
  /** Big-step names; a checkpoint's `step` indexes into this. */
  steps: string[]
  checkpoints: TourCheckpoint[]
}

const FIRST_QUOTE_CHECKPOINTS: TourCheckpoint[] = [
  // Step 1 · Create event — on the Events index and its #newEventModal.
  {
    step: 0,
    target: 'new-event-btn',
    mode: 'click',
    title: 'Start a new event',
    body: 'Every quote lives inside an event. Click **New event**.',
  },
  {
    step: 0,
    mode: 'intro',
    title: 'The new event form',
    body:
      "Just the basics of the job — who, when, where. Three fields matter today; the rest can be filled in later. Take a look, then we'll go field by field.",
  },
  {
    // First, because the real form puts the client select above the name field.
    // It is a <select> over the account's clients, not a text input, so the
    // engine's readiness check has to accept `change` as well as `input` — the
    // inline create path already dispatches `change` after adding a client
    // (app/views/events/partials/_scripts.html.erb).
    step: 0,
    target: 'ne-client',
    mode: 'input',
    title: "Who's it for?",
    body:
      "Pick a client, or add one — click **Add new client** and they're created right here, no detour to the Clients screen.",
  },
  {
    step: 0,
    target: 'ne-name',
    mode: 'input',
    title: 'Name the event',
    body:
      "Something you'll recognise in a list — the couple, the company, the occasion. Try **Wilson – Tran wedding**.",
  },
  {
    step: 0,
    target: 'ne-date',
    mode: 'input',
    title: 'When is it?',
    body: 'The date sets your bench days and delivery. Everything else on this form can wait.',
  },
  {
    // The one checkpoint whose click leaves the page: the modal's success
    // handler navigates to the created event's editor while a run is active
    // (app/javascript/dashboard/eventFormHandler.js,
    // docs/INTERACTIVE_TUTORIALS_PLAN.md §1.4). The advance has to be in
    // sessionStorage BEFORE that handler runs, or the editor resumes on this
    // checkpoint and docks against a target left behind on the index.
    step: 0,
    target: 'ne-save',
    mode: 'click',
    advanceImmediately: true,
    title: 'Save it',
    body: 'This creates the event and opens its quote.',
  },

  // Step 2 · Pricing details — the event editor's Details step.
  {
    step: 1,
    mode: 'intro',
    title: 'This is the quote editor',
    body:
      'Three steps across the top — **Details**, **Arrangements**, **Review & send**. Event details and pricing basis on the left; the running total on the right updates as you type.',
  },
  {
    step: 1,
    target: 'pf-markup',
    screen: 'details',
    mode: 'input',
    title: 'Set your markup',
    body:
      "Multiplies every stem's cost to get what the client pays. A common starting point is **2.5**.",
  },
  {
    // Before labour, because PricingFields.vue renders the buffer
    // (#slush-percentage, "Buffer (%)") above the labour rate.
    step: 1,
    target: 'pf-buffer',
    screen: 'details',
    mode: 'input',
    title: 'Add a buffer',
    body:
      'A percentage added on top of everything you have costed — flowers, labour, hire — so a stem that goes up in price or an extra hour on the bench comes out of the buffer, not your margin. Try **10**. It is itemised out of the quote total, and the summary on the right shows it as room to move rather than money to spend.',
  },
  {
    step: 1,
    target: 'pf-labour',
    screen: 'details',
    mode: 'input',
    title: "What's your hourly rate?",
    body:
      'Labour is charged per minute in studio, so set what an hour of your time is worth — e.g. **65**.',
  },
  {
    step: 1,
    target: 'qstep-arrangements',
    mode: 'click',
    title: 'On to arrangements',
    body:
      "Pricing is set once per event. Now the things you'll actually make — click **Arrangements**.",
  },

  // Step 3 · Arrangements.
  {
    step: 2,
    mode: 'intro',
    title: 'Arrangements',
    body:
      "Everything you'll make for this event, one card each: flowers, materials and labour per unit, times a quantity. Hire items sit below. The summary keeps tallying on the right.",
  },
  {
    step: 2,
    target: 'add-arrangement',
    screen: 'arrangements',
    mode: 'click',
    title: 'Add an arrangement',
    body: 'An arrangement is one thing you make, times a quantity. Click **Add arrangement**.',
  },
  {
    // A new card is added FOLDED (useArrangementEditor starts isExpanded false,
    // and BasicInfoForm renders inside `v-if="isExpanded"`), so the fields below
    // are not in the DOM until the florist opens it. Honouring the app rather
    // than auto-expanding it for the tour (plan Precedence rule 1) makes that
    // one more checkpoint.
    step: 2,
    target: 'arr-card',
    screen: 'arrangements',
    mode: 'click',
    title: 'Open it',
    body: 'Your new arrangement starts folded. Click it to open the card.',
  },
  {
    step: 2,
    target: 'arr-name',
    screen: 'arrangements',
    reveal: 'arr-card',
    mode: 'input',
    title: 'Name it',
    body: 'What the client will see on the quote — try **Bridal bouquet**.',
  },
  {
    step: 2,
    target: 'arr-labour',
    screen: 'arrangements',
    reveal: 'arr-card',
    mode: 'input',
    title: 'How long on the bench?',
    body: 'Minutes in studio per unit, e.g. **90**. This × your hourly rate is the labour line.',
  },
  {
    step: 2,
    target: 'add-flowers',
    screen: 'arrangements',
    reveal: 'arr-card',
    mode: 'click',
    title: 'Add a flower',
    body:
      'Stems × cost × your markup is what the client pays for it. Click **Add Flowers** and pick from your catalogue.',
  },
  {
    // An INTRO, deliberately, while the flower picker is open. The picker is a
    // modal below the overlay's z-index, and a targeted checkpoint here would
    // lay the scrim over it — every Add button greyed out and unclickable, and
    // the "Review & send" step button it would spotlight sitting under the
    // modal's own backdrop. An intro renders no scrim and no spotlight, so the
    // florist can use the picker; its button just advances the tour once they
    // have closed it. Found by test/system/interactive_tutorial_test.rb, which
    // is the only place the picker and the overlay meet.
    step: 2,
    mode: 'intro',
    cta: 'Next',
    dismiss: 'flowers-done',
    title: 'Pick a flower',
    body:
      'Choose a stem and click **Add** — as many as you like. No flowers in your catalogue yet, or want a new variety? Click **Add New Flower** at the top of the picker. **Done** closes it; then come back here.',
  },
  {
    // Read-only stop: the arrangement's own cost panel, now that it has a
    // stem, labour minutes and the event's pricing basis behind it.
    step: 2,
    target: 'arr-cost',
    screen: 'arrangements',
    reveal: 'arr-card',
    mode: 'look',
    title: 'What it costs',
    body:
      'Every number above lands here: stems at cost, labour at your hourly rate, then your markup and buffer — and the price per unit times the quantity. Change any of them and watch this move.',
  },
  {
    step: 2,
    target: 'qstep-review',
    mode: 'click',
    title: 'Review it',
    body: 'Materials and hire items work the same way — add them any time. Click **Review & send**.',
  },

  // Step 4 · Send for e-signature.
  {
    step: 3,
    mode: 'intro',
    title: 'Review & send',
    body:
      "Three documents from the same numbers — the **client quote**, your **florist worksheet**, and a **supplier order**. Flick between the tabs, then we'll send the quote.",
  },
  {
    // ReviewStep.vue enables this button on the client having an email plus at
    // least one arrangement, and that email is `clients.billing_email` — edited
    // on the CLIENT RECORD (app/views/clients/_form.html.erb), not on the
    // event, and reaching the editor via EventSerializer#serialize_client. The
    // handoff README read the disabled state as a missing STUDIO billing email
    // and would have sent the florist to the wrong screen.
    step: 3,
    target: 'send-btn',
    screen: 'review',
    mode: 'click',
    title: 'Send it for signature',
    body:
      "This is the client-facing quote. Click **Send for signature**. If it's greyed out, the client needs a **billing email** — add it on their client record and the button lights up.",
  },
  {
    // The send form, before the button on it (Hannah, 2026-09-08): the same
    // "take a look first" beat as the new-event form. Scrim-free, so the modal
    // reads as the app's own screen rather than a spotlit control.
    step: 3,
    mode: 'intro',
    title: 'The send form',
    body:
      "Your client's billing email is filled in from their record — change it here if this quote should go elsewhere. Add a personal note or attach a brief or mood board if you like. Take a look, then we'll send it.",
  },
  {
    step: 3,
    target: 'send-confirm',
    screen: 'review',
    alt: 'send-later',
    mode: 'click',
    title: 'Confirm the send',
    body:
      'Your client gets an email, signs on any device, and the event flips to **Signed**. Tick "send a copy to me first" if you\'d rather see it land before they do.',
  },
]

export const TOURS: Tour[] = [
  {
    id: 'first_quote',
    title: 'Your first quote',
    subtitle: 'Event → pricing → arrangements → e-sign',
    minutes: 6,
    steps: ['Create event', 'Pricing details', 'Arrangements', 'Send for e-signature'],
    checkpoints: FIRST_QUOTE_CHECKPOINTS,
  },
]
